<script setup lang="ts">
import type { PropertiesListingMapState } from '#imports';
import type { NavigationTab } from '~/components/ContextualNavigationHeader';
import type { WishlistsState } from '~/composables/useWishlists/wishlists';

import { useBreakpoints } from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { HEADER_OFFSET_CONFIG } from '~/composables/useDragToBottom/constants';
import { MixpanelProps } from '~/core/service/mixpanel';
import { useNavigationStore } from '~/store/navigationStore';
import { cn } from '~/utils';
import { screenSizesInPixels } from '~~/utils';
import { PROPERTIES_LISTING_METHODS } from '~~/utils/symbols';

import { ContextualNavigationHeader } from '~/components/ContextualNavigationHeader';

const route = useRoute();
const isMapView = computed<boolean>(() => route.path.includes('/imoveis'));

const headerDragOffsetY = useState<number>(
	'header-drag-offset-y',
	() => HEADER_OFFSET_CONFIG.MIN_OFFSET
);

const isClientMounted = ref(false);

const { smaller } = useBreakpoints(screenSizesInPixels);
const isMobile = smaller('lg');

onMounted(() => {
	isClientMounted.value = true;
	headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
});

watch(isMobile, isNowMobile => {
	if (!isNowMobile) {
		headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
	}
});

const { variant, transform } = useHeaderAnimation({
	headerHeightInPx: HEADER_OFFSET_CONFIG.DESKTOP_HEIGHT,
	variants: ['primary', 'primary']
});

const { loggedIn } = useUserSession();

useState<PropertiesListingMapState>('properties-listing-map', () => ({
	fetchedData: null,
	filters: {
		transactionType: 'sell',
		countPluscodes: [],
		propertyTypes: [],
		locationSlug: 'sao-paulo-sp-brasil'
	},
	pluscodes: {},
	mapConfig: {
		zoom: 14,
		maxZoom: 17,
		minZoom: 8,
		bounds: [],
		center: {
			lat: -23.585,
			lng: -46.685
		}
	}
}));

const wishlistsState = useState<WishlistsState>('wishlists');

const navigationTabs = computed<NavigationTab[]>(() => [
	{
		id: 'colecoes',
		label: 'Minha coleção',
		route: '/colecoes/minha-colecao',
		count: wishlistsState.value?.propertyCount ?? 0,
		// No SSR sempre mostra loading, no client mostra loading até ter dados reais
		showCountLoading:
			import.meta.server || !wishlistsState.value?.wishlists?.length
	},
	{ id: 'perfil', label: 'Meus dados', route: '/perfil' }
]);

const { trackMenuCollectionClicked } = useEvents();

function handleTabClick(tab: NavigationTab): void {
	// Determina os parâmetros baseados no ID da aba clicada
	const clickCollection = tab.id === 'colecoes';
	const clickUserInformation = tab.id === 'perfil';

	// Emite o evento personalizado de analytics usando Mixpanel
	trackMenuCollectionClicked({
		[MixpanelProps.CLICK_COLLECTION]: clickCollection,
		[MixpanelProps.CLICK_USER_INFORMATION]: clickUserInformation
	});
}

provide(PROPERTIES_LISTING_METHODS, {
	updateMapFilters: () => {}
});

const navigationStore = useNavigationStore();
const { propertyListSectionScrollY, isHeaderVisible } =
	storeToRefs(navigationStore);

const previousScrollY = ref(0);
const scrollDirection = ref<'up' | 'down'>('up');

watch(propertyListSectionScrollY, handlePropertyListSectionScrollY);

function handlePropertyListSectionScrollY(scrollY: number) {
	if (!isMobile.value) return;

	if (scrollY <= 0) {
		navigationStore.isHeaderVisible = true;
	} else if (scrollY > previousScrollY.value) {
		scrollDirection.value = 'down';
		navigationStore.isHeaderVisible = false;
	} else if (scrollY < previousScrollY.value) {
		scrollDirection.value = 'up';
		navigationStore.isHeaderVisible = true;
	}
	previousScrollY.value = Math.max(0, scrollY);
}

const contextualHeaderTranslateClass = computed<string>(() => {
	return isHeaderVisible.value
		? 'translate-y-0'
		: 'translate-y-[-60px] md:translate-y-[-80px]';
});

const computedClasses = computed(() => {
	return cn(
		`h-[${HEADER_OFFSET_CONFIG.MOBILE_HEIGHT}px] lg:h-[${HEADER_OFFSET_CONFIG.DESKTOP_HEIGHT}px]`,
		{
			'h-[3.1875rem]': !isHeaderVisible.value
		}
	);
});
</script>

<template>
	<div
		class="flex h-dvh flex-col overflow-hidden has-[[data-testid='onboarding-collections']]:h-full has-[[data-testid='onboarding-collections']]:overflow-auto [&:has([data-testid='onboarding-collections'])_[data-testid='portal-header']]:translate-y-0"
	>
		<div
			class="fixed top-0 z-[49] w-full divide-y divide-primary-light-4"
			:class="computedClasses"
		>
			<div
				:class="
					cn({
						'z-50 lg:z-[60]': isHeaderVisible,
						'z-50 -translate-y-full md:h-20': !isHeaderVisible
					})
				"
				class="relative flex h-[60px] w-full flex-col transition-transform duration-300 ease-in-out"
			>
				<PortalHeader
					:variant="variant"
					:transform="transform"
					:is-visible="true"
					:has-header-overlay="true"
					:class="[
						'h-[60px] md:h-20 lg:h-[60px]',
						{
							'z-50 lg:z-[60]': isMapView,
							'z-50': !isMapView
						}
					]"
					data-testid="portal-header"
				/>
			</div>
			<div
				class="relative flex w-full flex-col bg-primary transition-transform duration-300 ease-in-out"
				:class="contextualHeaderTranslateClass"
			>
				<ClientOnly>
					<ContextualNavigationHeader
						v-if="loggedIn"
						:tabs="navigationTabs"
						@tab-click="handleTabClick"
					/>
				</ClientOnly>
			</div>
		</div>

		<main
			class="flex grow flex-col overflow-hidden bg-white has-[[data-testid='onboarding-collections']]:overflow-auto"
			:class="{
				'relative h-full py-0': isMapView,
				'pt-[111px] md:pt-[120px]': isHeaderVisible,
				'pt-[3.1875rem] md:pt-[3.625rem]': !isHeaderVisible
			}"
		>
			<slot />
		</main>
	</div>
</template>
